import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAdmin } from '@vtex/raccoon-next'
import { STORAGE, useCache } from '../useCache'
import { useFetch } from '../useFetch'
import { FALLBACK_FEATURES, FeatureKeys, Features } from './constants'
import { mapFeatures } from './mapper'

const LOCAL_STORAGE_KEY = 'features'

export function useFeature() {
  const { fetcherWrapper } = useFetch()

  const { account, workspace, locale, production } = useAdmin()

  const language = locale?.split('-')[0] ?? ''

  const { get, set } = useCache()

  const queryClient = useQueryClient()

  const getFallback = () =>
    get(LOCAL_STORAGE_KEY, FALLBACK_FEATURES, STORAGE.LOCAL)

  const getFallbackByKey = (key: FeatureKeys) => getFallback()[key]

  const getFeature = <K extends FeatureKeys>(key: K): Features[K] => {
    const allFeatures = queryClient.getQueryData<Features>(['feature'])

    if (!allFeatures || !allFeatures[key]) return getFallbackByKey(key)

    try {
      const feature = JSON.parse(allFeatures[key] as string)
      return feature
    } catch {
      return allFeatures[key]
    }
  }

  async function fetcher(): Promise<Features> {
    return fetcherWrapper('/api/featurehub', {
      method: 'POST',
      body: JSON.stringify({
        account,
        workspace,
        production,
        userkey: account,
        language,
      }),
    })
      .then((r) => r.json())
      .then((r) => {
        const mapped = mapFeatures(r)
        set(LOCAL_STORAGE_KEY, mapped, STORAGE.LOCAL)
        return mapped
      })
      .catch(() => {
        return getFallback()
      })
  }

  return {
    ...useQuery<Features>({
      queryKey: ['feature'],
      queryFn: fetcher,
    }),
    getFeature,
  }
}
